import {
	Callback18Regular,
	WhatsApp20Regular,
	WhatsApp24Regular,
} from "@/common/icons";
import { InteractionType } from "@/common/types";
import {
	InteractionDto,
	InteractionFilterData,
	InteractionOperations,
	InteractionStatus,
	Queue,
} from "@/repository/models";
import {
	Call20Regular,
	Call24Regular,
	CallPark20Regular,
	Chat20Regular,
	Chat24Regular,
	ChatMultiple20Regular,
	ChatMultiple24Regular,
	Mail20Regular,
	Mail24Regular,
	Voicemail20Regular,
	Voicemail24Regular,
} from "@fluentui/react-icons";
import { captureError } from "./sentryHelper";
import { InteractionPanelItemDescription } from "@/components/organisms/interaction/types";
import { Handled15Regular } from "../icons/handledIcon/handledIcon";
import { Unhandled15Regular } from "../icons/unhandledIcon/unhandledIcon";
import { tokens } from "@fluentui/react-components";
import { IntlShape } from "react-intl";
import { WebchatDefaultCustomerName } from "../constants";
import {
	AssignInteractionRequest,
	AssignInteractionResponse,
} from "@/repository/models/assignInteraction";
import { CallSession } from "@/common/models/callSession";
import { AppCacheActions, CacheKey } from "@/common/stores/persistingStores/app";
import { AgentInteractionStatus } from "@/common/constants/callSession";

export const handlePickUpInteraction = (
	interactionId: string,
	pickUpFn: (id: string) => Promise<boolean>,
	afterPickUpFn: (isPickedUp: boolean) => void,
	pickUpErrorFn: () => void
) => {
	pickUpFn(interactionId)
		.then((isPickedUp) => {
			afterPickUpFn(isPickedUp);
		})
		.catch((err: Error) => {
			captureError(err, "Pickup failed", { interactionId });
			pickUpErrorFn();
		});
};

export const handleAssignInteraction = (
	data: AssignInteractionRequest,
	assignFn: (
		data: AssignInteractionRequest
	) => Promise<AssignInteractionResponse>,
	afterAssignFn: (assignResponse: AssignInteractionResponse) => void,
	assignErrorFn: () => void
) => {
	assignFn(data)
		.then((assignResponse) => {
			afterAssignFn(assignResponse);
		})
		.catch((err: Error) => {
			captureError(err, "Assign failed", data);
			assignErrorFn();
		});
};

export const agentStatusAllowsPickingUpAndResumingInteraction = (
	interaction: { type: InteractionType },
	agentHasInteractionInOffering: boolean,
	agentHasCallInProgressOrTextInOffering: boolean
) => {
	const isTextInteraction = textInteractionTypes.includes(interaction.type);

	return (
		(isTextInteraction && !agentHasInteractionInOffering) ||
		!agentHasCallInProgressOrTextInOffering
	);
};

export const interactionCanBeResumed = (
	interaction: { type: InteractionType; status: InteractionStatus },
	agentHasInteractionInOffering: boolean,
	agentHasCallInProgressOrTextInOffering: boolean
) => {
	return (
		interaction.status === InteractionStatus.Parked &&
		agentStatusAllowsPickingUpAndResumingInteraction(
			interaction,
			agentHasInteractionInOffering,
			agentHasCallInProgressOrTextInOffering
		)
	);
};

export const interactionCanBePickedUp = (
	interaction: {
		type: InteractionType;
		status: InteractionStatus;
		isPickupEnabled: boolean;
	},
	agentHasInteractionInOffering: boolean,
	agentHasCallInProgressOrTextInOffering: boolean
) => {
	const result = 
	    (interaction.type == InteractionType.Voicemail || 
		(interaction.status !== InteractionStatus.Offering &&
		interaction.status !== InteractionStatus.CallbackRequest &&
		interaction.status !== InteractionStatus.CallbackOffering &&
		interaction.status !== InteractionStatus.Parked &&
		interaction.status !== InteractionStatus.ColdTransferRinging &&
		interaction.isPickupEnabled)) &&
		agentStatusAllowsPickingUpAndResumingInteraction(
			interaction,
			agentHasInteractionInOffering,
			agentHasCallInProgressOrTextInOffering
		);

	return result;
};

export const getVoiceInteractionIcon = (interaction: InteractionDto) => {
	switch (interaction.status) {
		case InteractionStatus.Parked:
			return <CallPark20Regular />;
		case InteractionStatus.CallbackRequest:
		case InteractionStatus.CallbackOffering:
			return <Callback18Regular />;
	}

	return <Call20Regular />;
};

export const mapInteractionStatusToPanelStatus = (
	interactionStatus: InteractionStatus
) => {
	switch (interactionStatus) {
		case InteractionStatus.Offering:
		case InteractionStatus.CallbackOffering:
			return InteractionPanelItemDescription.Offering;
		case InteractionStatus.Fallback:
			return InteractionPanelItemDescription.Fallback;
		default:
			return InteractionPanelItemDescription.Waiting;
	}
};

export const agentCanViewInteraction = (
	queues: Queue[],
	interaction: InteractionDto
) => {
	const interactionQueue = queues.find(
		(x) => x.id === interaction.queueId
	);

	if (!interactionQueue) {
		return false;
	}

	return (
		interactionQueue.isSupervisor ||
		(interactionQueue.isOperator &&
			interactionQueue.hasAllInteractionVisibilityRight)
	);
};

export const isInteractionFiltered = (
	filterData: InteractionFilterData,
	interaction: InteractionDto
) => {
	if (
		!filterData ||
		(filterData.queueIds.length === 0 && filterData.types.length === 0) ||
		interaction.changeType === InteractionOperations.Removed
	) {
		return false;
	}

	return (
		(filterData.queueIds.length > 0 &&
			!filterData.queueIds.includes(interaction.queueId)) ||
		(filterData.types.length > 0 &&
			!filterData.types.includes(interaction.type.toString()))
	);
};

export const InteractionsIconMapMedium = {
	[InteractionType.Call]: <Call20Regular />,
	[InteractionType.Generic]: <Chat20Regular />,
	[InteractionType.Email]: <Mail20Regular />,
	[InteractionType.Voicemail]: <Voicemail20Regular />,
	[InteractionType.WhatsApp]: <WhatsApp20Regular />,
	[InteractionType.Webchat]: <ChatMultiple20Regular />,
};

export const InteractionsIconMapBig = {
	[InteractionType.Call]: <Call24Regular />,
	[InteractionType.Generic]: <Chat24Regular />,
	[InteractionType.Email]: <Mail24Regular />,
	[InteractionType.Voicemail]: <Voicemail24Regular />,
	[InteractionType.WhatsApp]: <WhatsApp24Regular />,
	[InteractionType.Webchat]: <ChatMultiple24Regular />,
};

export const getHandlingStatusIcon = (isHandled: boolean) => {
	return isHandled ? (
		<Handled15Regular strokeColor={tokens.colorNeutralForeground2Link} />
	) : (
		<Unhandled15Regular strokeColor={tokens.colorNeutralForeground2Link} />
	);
};

export const getInteractionName = (
	type: InteractionType,
	name: string,
	intl: IntlShape
) => {
	if (type === InteractionType.Webchat && name === WebchatDefaultCustomerName) {
		return intl.formatMessage({
			id: "Interaction.WebchatDefaultCustomerName",
		});
	}
	return name;
};

export const isUnknownCustomerContact = (contactInfo?: string) =>
	!contactInfo || contactInfo === WebchatDefaultCustomerName;

/** A webchat customer has no phone number or SIP address, whatever name they typed. */
export const hasNoCallbackTarget = (item: {
	contactInfo?: string;
	isCallerWebchat?: boolean;
}) => !!item.isCallerWebchat || isUnknownCustomerContact(item.contactInfo);

export const messagingInteractionTypes = [
	InteractionType.Generic,
	InteractionType.Email,
	InteractionType.WhatsApp,
	InteractionType.Webchat,
];

export const textInteractionTypes = [
	InteractionType.Email,
	InteractionType.WhatsApp,
	InteractionType.Webchat,
];

export const chatInteractionTypes = [
	InteractionType.WhatsApp,
	InteractionType.Webchat,
];

export const cleanupResourcesCache = (
	callSession: CallSession,
	appStoreActions: AppCacheActions
) => {
	switch (callSession.state) {
		case AgentInteractionStatus.Offering:
		case AgentInteractionStatus.SimultaneousOffering:
		case AgentInteractionStatus.ReceivingWarmTransferInvitation:
			appStoreActions.removeCaches([
				CacheKey.RemoveDynamicUrlCallId,
				CacheKey.AcceptedInteractionId,
			]);
			break;
		case AgentInteractionStatus.Active:
		case AgentInteractionStatus.AcceptedWarmTransferInvitation:
			appStoreActions.removeCaches([
				CacheKey.OfferingDynamicUrlCallId,
				CacheKey.RemoveDynamicUrlCallId,
			]);
			break;
		case AgentInteractionStatus.Terminated:
			appStoreActions.removeCaches([
				CacheKey.OfferingDynamicUrlCallId,
				CacheKey.AcceptedInteractionId,
			]);
			break;
	}
};
